import moment from 'moment';
import { colors } from '../../../../../../colors';
import { SingleProfitCalculationApiResponse } from '../../../../types';
import { formatPercentage } from '../../../../../../helpers/NumberFormatter';
import { useMemo } from 'react';

const ProfitInformation = ({
  data,
}: {
  data: SingleProfitCalculationApiResponse;
}) => {
  const startData = moment(
    data.savedData.higherTNormalCandles[0].startTimestamp,
  ).format('DD MMM YYYY');

  const endDate = moment(
    data.savedData.higherTNormalCandles[
      data.savedData.higherTNormalCandles.length - 1
    ].startTimestamp,
  ).format('DD MMM YYYY');

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-evenly',
        width: '100%',
      }}
    >
      <div>
        <p
          style={{
            color: colors.white,
            fontSize: 26,
            fontWeight: 'bold',
            marginTop: 0,
            marginBottom: -10,
          }}
        >
          Summary
        </p>
        <span
          style={{
            margin: 0,
            color: colors.gray,
            fontSize: 14,
          }}
        >
          <span>{startData}</span>
          {' - '}
          <span>{endDate}</span>
        </span>
        <TimeFrames data={data} />
        <ComplexParams data={data} />
        <TransactionsInfo data={data} />
        <TotalProfits data={data} />
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'end',
          flexDirection: 'column',
          textAlign: 'end',
        }}
      >
        <CurrentProfits data={data} />
        <StableProfits data={data} />
        <AssetProfits data={data} />
        <AverageStableProfits data={data} />
        <AverageAssetProfits data={data} />
      </div>
    </div>
  );
};

const TimeFrames = ({ data }: { data: SingleProfitCalculationApiResponse }) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        marginTop: 8,
      }}
    >
      <p
        style={{
          color: colors.gray,
          fontSize: 14,
          margin: 0,
        }}
      >
        TimeFrames
      </p>
      <div style={{ display: 'flex', columnGap: 16 }}>
        <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
          <span>higherT:</span>{' '}
          <span
            style={{
              color: colors.white,
              fontSize: 16,
            }}
          >
            {data.higherTimeFrame}
          </span>
        </span>
        <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
          <span>shorterT:</span>{' '}
          <span
            style={{
              color: colors.white,
              fontSize: 16,
            }}
          >
            {data.shorterTimeFrame}
          </span>
        </span>
        <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
          <span>multiT:</span>{' '}
          <span
            style={{
              color: colors.white,
              fontSize: 16,
            }}
          >
            {data.multiOrderTimeFrame}
          </span>
        </span>
      </div>
    </div>
  );
};

const ComplexParams = ({
  data,
}: {
  data: SingleProfitCalculationApiResponse;
}) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        marginTop: 16,
      }}
    >
      <p
        style={{
          color: colors.gray,
          fontSize: 14,
          margin: 0,
        }}
      >
        Complex Params
      </p>
      <div style={{ display: 'flex', columnGap: 16 }}>
        <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
          <span>cbrt:</span>{' '}
          <span
            style={{
              color: colors.white,
              fontSize: 16,
            }}
          >
            {data.cbrt}
          </span>
        </span>
        <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
          <span>shorterT Interval:</span>{' '}
          <span
            style={{
              color: colors.white,
              fontSize: 16,
            }}
          >
            {data.shorterTimeFrameInterval}
          </span>
        </span>
      </div>

      <div style={{ display: 'flex', columnGap: 16 }}>
        <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
          <span>Use cbrt range</span>{' '}
          <span
            style={{
              color: colors.white,
              fontSize: 16,
            }}
          >
            {data.supportCbrtRange ? 'Yes' : 'No'}
          </span>
        </span>
        <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
          <span>Use mrc state</span>{' '}
          <span
            style={{
              color: colors.white,
              fontSize: 16,
            }}
          >
            {data.supportMrcState ? 'Yes' : 'No'}
          </span>
        </span>
      </div>
    </div>
  );
};

const CurrentProfits = ({
  data,
}: {
  data: SingleProfitCalculationApiResponse;
}) => {
  const priceChange = data.currentPriceChange;
  const currentProfit = data.currentProfit;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <p
        style={{
          color: colors.gray,
          fontSize: 14,
          margin: 0,
        }}
      >
        Current Profits
      </p>
      <div
        style={{
          display: 'flex',
          columnGap: 16,
          flexDirection: 'column',
          alignItems: 'end',
        }}
      >
        <div style={{ display: 'flex', columnGap: 16 }}>
          <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
            <span>Profit:</span>{' '}
            <span
              style={{
                color: currentProfit < 0 ? colors.red : colors.green,
                fontSize: 16,
              }}
            >
              {formatPercentage(currentProfit, 2, false, 'always')}
            </span>
          </span>
        </div>

        <div style={{ display: 'flex', columnGap: 16 }}>
          <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
            <span>Price Change:</span>{' '}
            <span
              style={{
                color: currentProfit < 0 ? colors.red : colors.green,
                fontSize: 16,
              }}
            >
              {formatPercentage(priceChange, 2, false, 'always')}
            </span>
          </span>
        </div>
      </div>
    </div>
  );
};

const AverageStableProfits = ({
  data,
}: {
  data: SingleProfitCalculationApiResponse;
}) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        marginTop: 16,
      }}
    >
      <p
        style={{
          color: colors.gray,
          fontSize: 14,
          margin: 0,
        }}
      >
        Average Stable Profits
      </p>
      <div
        style={{
          display: 'flex',
          columnGap: 16,
          flexDirection: 'column',
          alignItems: 'end',
        }}
      >
        <div style={{ display: 'flex', columnGap: 16 }}>
          <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
            <span>1d:</span>{' '}
            <span
              style={{
                color:
                  data.averageStableProfit.daily < 0
                    ? colors.red
                    : colors.green,
                fontSize: 16,
              }}
            >
              {formatPercentage(data.averageStableProfit.daily)}
            </span>
          </span>
          <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
            <span>1w:</span>{' '}
            <span
              style={{
                color:
                  data.averageStableProfit.weekly < 0
                    ? colors.red
                    : colors.green,
                fontSize: 16,
              }}
            >
              {formatPercentage(data.averageStableProfit.weekly)}
            </span>
          </span>
        </div>

        <div style={{ display: 'flex', columnGap: 16 }}>
          <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
            <span>1M:</span>{' '}
            <span
              style={{
                color:
                  data.averageStableProfit.monthly < 0
                    ? colors.red
                    : colors.green,
                fontSize: 16,
              }}
            >
              {formatPercentage(data.averageStableProfit.monthly)}
            </span>
          </span>
          <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
            <span>1Y:</span>{' '}
            <span
              style={{
                color:
                  data.averageStableProfit.yearly < 0
                    ? colors.red
                    : colors.green,
                fontSize: 16,
              }}
            >
              {formatPercentage(data.averageStableProfit.yearly)}
            </span>
          </span>
        </div>
      </div>
    </div>
  );
};

const AverageAssetProfits = ({
  data,
}: {
  data: SingleProfitCalculationApiResponse;
}) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        marginTop: 16,
      }}
    >
      <p
        style={{
          color: colors.gray,
          fontSize: 14,
          margin: 0,
        }}
      >
        Average Asset Profits
      </p>
      <div
        style={{
          display: 'flex',
          columnGap: 16,
          flexDirection: 'column',
          alignItems: 'end',
        }}
      >
        <div style={{ display: 'flex', columnGap: 16 }}>
          <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
            <span>1d:</span>{' '}
            <span
              style={{
                color:
                  data.averageAssetProfit.daily < 0 ? colors.red : colors.green,
                fontSize: 16,
              }}
            >
              {formatPercentage(data.averageAssetProfit.daily)}
            </span>
          </span>
          <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
            <span>1w:</span>{' '}
            <span
              style={{
                color:
                  data.averageAssetProfit.weekly < 0
                    ? colors.red
                    : colors.green,
                fontSize: 16,
              }}
            >
              {formatPercentage(data.averageAssetProfit.weekly)}
            </span>
          </span>
        </div>

        <div style={{ display: 'flex', columnGap: 16 }}>
          <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
            <span>1M:</span>{' '}
            <span
              style={{
                color:
                  data.averageAssetProfit.monthly < 0
                    ? colors.red
                    : colors.green,
                fontSize: 16,
              }}
            >
              {formatPercentage(data.averageAssetProfit.monthly)}
            </span>
          </span>
          <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
            <span>1Y:</span>{' '}
            <span
              style={{
                color:
                  data.averageAssetProfit.yearly < 0
                    ? colors.red
                    : colors.green,
                fontSize: 16,
              }}
            >
              {formatPercentage(data.averageAssetProfit.yearly)}
            </span>
          </span>
        </div>
      </div>
    </div>
  );
};

const StableProfits = ({
  data,
}: {
  data: SingleProfitCalculationApiResponse;
}) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        marginTop: 16,
      }}
    >
      <p
        style={{
          color: colors.gray,
          fontSize: 14,
          margin: 0,
        }}
      >
        Stable Profits
      </p>
      <div
        style={{
          display: 'flex',
          columnGap: 16,
          flexDirection: 'column',
          alignItems: 'end',
        }}
      >
        <div style={{ display: 'flex', columnGap: 16 }}>
          <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
            <span>1d:</span>{' '}
            <span
              style={{
                color:
                  data.stableProfitSince.day < 0 ? colors.red : colors.green,
                fontSize: 16,
              }}
            >
              {formatPercentage(data.stableProfitSince.day)}
            </span>
          </span>
          <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
            <span>1w:</span>{' '}
            <span
              style={{
                color:
                  data.stableProfitSince.week < 0 ? colors.red : colors.green,
                fontSize: 16,
              }}
            >
              {formatPercentage(data.stableProfitSince.week)}
            </span>
          </span>
        </div>

        <div style={{ display: 'flex', columnGap: 16 }}>
          <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
            <span>1M:</span>{' '}
            <span
              style={{
                color:
                  data.stableProfitSince.month < 0 ? colors.red : colors.green,
                fontSize: 16,
              }}
            >
              {formatPercentage(data.stableProfitSince.month)}
            </span>
          </span>
          <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
            <span>1Y:</span>{' '}
            <span
              style={{
                color:
                  data.stableProfitSince.year < 0 ? colors.red : colors.green,
                fontSize: 16,
              }}
            >
              {formatPercentage(data.stableProfitSince.year)}
            </span>
          </span>
        </div>

        <div style={{ display: 'flex', columnGap: 16 }}>
          <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
            <span>2Y:</span>{' '}
            <span
              style={{
                color:
                  data.stableProfitSince.twoYear < 0
                    ? colors.red
                    : colors.green,
                fontSize: 16,
              }}
            >
              {formatPercentage(data.stableProfitSince.twoYear)}
            </span>
          </span>
          <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
            <span>5Y:</span>{' '}
            <span
              style={{
                color:
                  data.stableProfitSince.FiveYear < 0
                    ? colors.red
                    : colors.green,
                fontSize: 16,
              }}
            >
              {formatPercentage(data.stableProfitSince.FiveYear)}
            </span>
          </span>
        </div>
      </div>
    </div>
  );
};

const AssetProfits = ({
  data,
}: {
  data: SingleProfitCalculationApiResponse;
}) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        marginTop: 16,
      }}
    >
      <p
        style={{
          color: colors.gray,
          fontSize: 14,
          margin: 0,
        }}
      >
        Asset Profits
      </p>
      <div
        style={{
          display: 'flex',
          columnGap: 16,
          flexDirection: 'column',
          alignItems: 'end',
        }}
      >
        <div style={{ display: 'flex', columnGap: 16 }}>
          <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
            <span>1d:</span>{' '}
            <span
              style={{
                color:
                  data.assetProfitSince.day < 0 ? colors.red : colors.green,
                fontSize: 16,
              }}
            >
              {formatPercentage(data.assetProfitSince.day)}
            </span>
          </span>
          <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
            <span>1w:</span>{' '}
            <span
              style={{
                color:
                  data.assetProfitSince.week < 0 ? colors.red : colors.green,
                fontSize: 16,
              }}
            >
              {formatPercentage(data.assetProfitSince.week)}
            </span>
          </span>
        </div>

        <div style={{ display: 'flex', columnGap: 16 }}>
          <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
            <span>1M:</span>{' '}
            <span
              style={{
                color:
                  data.assetProfitSince.month < 0 ? colors.red : colors.green,
                fontSize: 16,
              }}
            >
              {formatPercentage(data.assetProfitSince.month)}
            </span>
          </span>
          <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
            <span>1Y:</span>{' '}
            <span
              style={{
                color:
                  data.assetProfitSince.year < 0 ? colors.red : colors.green,
                fontSize: 16,
              }}
            >
              {formatPercentage(data.assetProfitSince.year)}
            </span>
          </span>
        </div>

        <div style={{ display: 'flex', columnGap: 16 }}>
          <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
            <span>2Y:</span>{' '}
            <span
              style={{
                color:
                  data.assetProfitSince.twoYear < 0 ? colors.red : colors.green,
                fontSize: 16,
              }}
            >
              {formatPercentage(data.assetProfitSince.twoYear)}
            </span>
          </span>
          <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
            <span>5Y:</span>{' '}
            <span
              style={{
                color:
                  data.assetProfitSince.FiveYear < 0
                    ? colors.red
                    : colors.green,
                fontSize: 16,
              }}
            >
              {formatPercentage(data.assetProfitSince.FiveYear)}
            </span>
          </span>
        </div>
      </div>
    </div>
  );
};

const TransactionsInfo = ({
  data,
}: {
  data: SingleProfitCalculationApiResponse;
}) => {
  const isCoreTransaction = (transactionType: string) =>
    !transactionType.includes('TAKE_PROFIT');

  const averageProfit = useMemo(() => {
    const coreTransactions = data.transactions.filter((t) =>
      isCoreTransaction(t.transactionType),
    );

    return (
      coreTransactions.reduce(
        (acc, curr) =>
          acc +
          ((curr.orderType === 'BUY'
            ? curr.profitInAsset
            : curr.profitInUsdt) || 0),
        0,
      ) / coreTransactions.length
    );
  }, [data.transactions]);

  const averageTransactionDuration = useMemo(() => {
    if (!data.transactions?.length) {
      return 0;
    }

    return (
      data.transactions.reduce((acc, curr, index, self) => {
        if (index === 0) {
          return acc;
        }

        const transactionTimestamp =
          curr.transactionTimestamp ?? curr.timestamp;
        const previousTransactionTimestamp =
          self[index - 1].transactionTimestamp ?? self[index - 1].timestamp;

        return acc + (transactionTimestamp - previousTransactionTimestamp);
      }, 0) / data.transactions.length
    );
  }, [data.transactions]);

  const averageTransactionDurationLabel = useMemo(() => {
    const days = Math.ceil(averageTransactionDuration / (1000 * 60 * 60 * 24));
    return `${days} ${days === 1 ? 'day' : 'days'}`;
  }, [averageTransactionDuration]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        marginTop: 16,
      }}
    >
      <p
        style={{
          color: colors.gray,
          fontSize: 14,
          margin: 0,
        }}
      >
        Transactions
      </p>
      <div style={{ display: 'flex', columnGap: 16, flexDirection: 'column' }}>
        <div style={{ display: 'flex', columnGap: 16 }}>
          <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
            <span>Amount:</span>{' '}
            <span
              style={{
                color: colors.white,
                fontSize: 16,
              }}
            >
              {data.amountOfOrders}
            </span>
          </span>
          <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
            <span>Winrate:</span>{' '}
            <span
              style={{
                color: colors.white,
                fontSize: 16,
              }}
            >
              {formatPercentage(
                data.amountOfSuccessfulOrders / (data.amountOfOrders - 1),
              )}
            </span>
          </span>
        </div>

        <div style={{ display: 'flex', columnGap: 16 }}>
          <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
            <span># Take Profit:</span>{' '}
            <span
              style={{
                color: colors.white,
                fontSize: 16,
              }}
            >
              {data.amountOfTakeProfitOrders}
            </span>
          </span>
          <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
            <span># Stop Loss:</span>{' '}
            <span
              style={{
                color: colors.white,
                fontSize: 16,
              }}
            >
              {data.amountOfStopLossOrders}
            </span>
          </span>
        </div>

        <div style={{ display: 'flex', columnGap: 16 }}>
          <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
            <span>Wins:</span>{' '}
            <span
              style={{
                color: colors.white,
                fontSize: 16,
              }}
            >
              {data.amountOfSuccessfulOrders}
            </span>
          </span>
          <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
            <span>Losses:</span>{' '}
            <span
              style={{
                color: colors.white,
                fontSize: 16,
              }}
            >
              {data.amountOfFailedOrders}
            </span>
          </span>
        </div>

        <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
          <span>Average profit:</span>{' '}
          <span
            style={{
              color: averageProfit < 0 ? colors.red : colors.green,
              fontSize: 16,
            }}
          >
            {formatPercentage(averageProfit)}
          </span>
        </span>
        <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
          <span>Average transaction duration:</span>{' '}
          <span style={{ fontSize: 16, color: colors.white }}>
            {averageTransactionDurationLabel}
          </span>
        </span>
      </div>
    </div>
  );
};

const TotalProfits = ({
  data,
}: {
  data: SingleProfitCalculationApiResponse;
}) => {
  const startPrice = data.savedData.higherTNormalCandles[0].close;

  const endPrice =
    data.savedData.higherTNormalCandles[
      data.savedData.higherTNormalCandles.length - 1
    ].close;

  const totalPriceChange = (endPrice - startPrice) / startPrice;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        marginTop: 16,
      }}
    >
      <p
        style={{
          color: colors.gray,
          fontSize: 14,
          margin: 0,
        }}
      >
        Total Profits
      </p>
      <div style={{ display: 'flex', columnGap: 16, flexDirection: 'column' }}>
        <div
          style={{ display: 'flex', flexDirection: 'column', columnGap: 16 }}
        >
          <p style={{ margin: 0, marginTop: -5, color: colors.gray }}>
            <span>Stable Profit:</span>{' '}
            <span
              style={{
                color:
                  data.totalTradingStableProfit < 0 ? colors.red : colors.green,
                fontSize: 16,
              }}
            >
              {formatPercentage(data.totalTradingStableProfit)}
            </span>
          </p>
          <p style={{ margin: 0, marginTop: -5, color: colors.gray }}>
            <span>Asset Profit:</span>{' '}
            <span
              style={{
                color:
                  data.totalTradingAssetProfit < 0 ? colors.red : colors.green,
                fontSize: 16,
              }}
            >
              {formatPercentage(data.totalTradingAssetProfit)}
            </span>
          </p>
        </div>

        <div style={{ display: 'flex', columnGap: 16 }}>
          <span style={{ margin: 0, marginTop: -5, color: colors.gray }}>
            <span>Price Change:</span>{' '}
            <span
              style={{
                color: totalPriceChange < 0 ? colors.red : colors.green,
                fontSize: 16,
              }}
            >
              {formatPercentage(totalPriceChange, 2, false, 'always')}
            </span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProfitInformation;

/**
 * I want a summary of all information here
 *      timeframe, complex params
 *      Profits last year, month, week, day
 *      average profit per year, month, week, day
 *      winrate amount of orders, average profit per transaction
 *      amount of transactions
 *      start date
 * total stable/asset profit
 * Nap profit, profit since first transaction
 */
